import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToGraphQLProps {}

// GraphQL schema generation options
type SchemaGenerationStyle = 'basic' | 'nullable' | 'comprehensive';
type NamingConvention = 'PascalCase' | 'camelCase';

const JsonToGraphQL: React.FC<JsonToGraphQLProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the GraphQL output
  const [graphqlOutput, setGraphqlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the schema name
  const [schemaName, setSchemaName] = useState<string>('MySchema');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for schema generation style
  const [generationStyle, setGenerationStyle] = useState<SchemaGenerationStyle>('basic');
  // State for naming convention
  const [namingConvention, setNamingConvention] = useState<NamingConvention>('PascalCase');
  // State to track if the conversion happened
  const [isGraphqlGenerated, setIsGraphqlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsGraphqlGenerated(false);
  };

  // Handle schema name change
  const handleSchemaNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSchemaName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsGraphqlGenerated(false);
    setGraphqlOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setGraphqlOutput('');
    setError('');
    setSchemaName('MySchema');
    setGenerationStyle('basic');
    setNamingConvention('PascalCase');
    setIsGraphqlGenerated(false);
  };

  // Convert a string to PascalCase
  const toPascalCase = (str: string): string => {
    return str
      .replace(/[^a-zA-Z0-9]/g, ' ')
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  };

  // Convert a string to camelCase
  const toCamelCase = (str: string): string => {
    const pascal = toPascalCase(str);
    return pascal.charAt(0).toLowerCase() + pascal.slice(1);
  };

  // Format a field name according to the selected naming convention
  const formatFieldName = (name: string): string => {
    return namingConvention === 'PascalCase' ? toPascalCase(name) : toCamelCase(name);
  };

  // Determine GraphQL type from a JavaScript value
  const determineGraphQLType = (value: any, key: string, isNested: boolean = false): string => {
    if (value === null || value === undefined) {
      return 'String'; // Default to String for null values
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[String]'; // Default to String array for empty arrays
      }
      const itemType = determineGraphQLType(value[0], key, true);
      return `[${itemType}]`;
    }

    const type = typeof value;
    
    switch (type) {
      case 'string':
        return 'String';
      case 'number':
        return Number.isInteger(value) ? 'Int' : 'Float';
      case 'boolean':
        return 'Boolean';
      case 'object':
        // For nested objects, we'll create a new type
        const typeName = namingConvention === 'PascalCase' 
          ? toPascalCase(key) 
          : toPascalCase(key); // Always use PascalCase for type names
        
        // If it's a nested array item, we don't want to add "Item" to every array item
        return isNested ? typeName : typeName;
      default:
        return 'String';
    }
  };

  // Generate GraphQL schema for a nested object
  const generateNestedSchema = (
    obj: any, 
    typeName: string, 
    schemas: Map<string, string[]>, 
    processedObjects: Set<string> = new Set()
  ): void => {
    if (processedObjects.has(JSON.stringify(obj))) {
      return; // Avoid circular references
    }
    
    processedObjects.add(JSON.stringify(obj));
    
    if (!schemas.has(typeName)) {
      schemas.set(typeName, []);
    }
    
    const fields = schemas.get(typeName)!;
    
    Object.entries(obj).forEach(([key, value]) => {
      const fieldName = formatFieldName(key);
      
      if (value === null || value === undefined) {
        fields.push(`  ${fieldName}: String${generationStyle === 'nullable' ? '' : '!'}`);
        return;
      }
      
      if (Array.isArray(value)) {
        if (value.length === 0) {
          fields.push(`  ${fieldName}: [String]${generationStyle === 'nullable' ? '' : '!'}`);
        } else {
          const firstItem = value[0];
          
          if (typeof firstItem === 'object' && firstItem !== null) {
            // Create a type for array items
            const itemTypeName = namingConvention === 'PascalCase' 
              ? toPascalCase(key) 
              : toPascalCase(key);
            
            fields.push(`  ${fieldName}: [${itemTypeName}]${generationStyle === 'nullable' ? '' : '!'}`);
            generateNestedSchema(firstItem, itemTypeName, schemas, processedObjects);
          } else {
            const itemType = determineGraphQLType(firstItem, key);
            fields.push(`  ${fieldName}: [${itemType}]${generationStyle === 'nullable' ? '' : '!'}`);
          }
        }
      } else if (typeof value === 'object') {
        const nestedTypeName = namingConvention === 'PascalCase' 
          ? toPascalCase(key) 
          : toPascalCase(key);
        
        fields.push(`  ${fieldName}: ${nestedTypeName}${generationStyle === 'nullable' ? '' : '!'}`);
        generateNestedSchema(value, nestedTypeName, schemas, processedObjects);
      } else {
        const type = determineGraphQLType(value, key);
        fields.push(`  ${fieldName}: ${type}${generationStyle === 'nullable' ? '' : '!'}`);
      }
    });
  };

  // Convert JSON to GraphQL schema
  const convertJsonToGraphQL = () => {
    setError('');
    setIsLoading(true);
    setIsGraphqlGenerated(false);
    setGraphqlOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Check if the input is an object
      if (typeof jsonData !== 'object' || jsonData === null || Array.isArray(jsonData)) {
        setError('Input must be a valid JSON object (not an array or primitive value).');
        setIsLoading(false);
        return;
      }

      // Store all the type definitions
      const schemas = new Map<string, string[]>();
      
      // Generate schema for the root object
      generateNestedSchema(jsonData, schemaName, schemas);
      
      // Build the final GraphQL schema string
      let schemaString = '';
      
      // Add scalar types if we're using comprehensive mode
      if (generationStyle === 'comprehensive') {
        schemaString += '# Scalar types\n';
        schemaString += 'scalar Date\n';
        schemaString += 'scalar DateTime\n\n';
      }
      
      // Build type definitions
      schemas.forEach((fields, typeName) => {
        schemaString += `type ${typeName} {\n`;
        schemaString += fields.join('\n');
        schemaString += '\n}\n\n';
      });
      
      // Add Query type
      schemaString += `type Query {\n`;
      schemaString += `  get${schemaName}: ${schemaName}${generationStyle === 'nullable' ? '' : '!'}\n`;
      
      if (generationStyle === 'comprehensive') {
        schemaString += `  get${schemaName}s: [${schemaName}!]!\n`;
        schemaString += `  get${schemaName}ById(id: ID!): ${schemaName}\n`;
      }
      
      schemaString += `}\n\n`;
      
      // Add Mutation type if in comprehensive mode
      if (generationStyle === 'comprehensive') {
        schemaString += `type Mutation {\n`;
        schemaString += `  create${schemaName}(input: ${schemaName}Input!): ${schemaName}!\n`;
        schemaString += `  update${schemaName}(id: ID!, input: ${schemaName}Input!): ${schemaName}!\n`;
        schemaString += `  delete${schemaName}(id: ID!): Boolean!\n`;
        schemaString += `}\n\n`;
        
        // Add Input type for mutations
        schemaString += `input ${schemaName}Input {\n`;
        
        // Convert the fields to input fields
        const inputFields = schemas.get(schemaName)!.map(field => {
          // Remove the exclamation mark for input fields
          return field.replace(/!$/, '');
        });
        
        schemaString += inputFields.join('\n');
        schemaString += '\n}\n';
      }
      
      setGraphqlOutput(schemaString);
      setIsGraphqlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating GraphQL schema: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy GraphQL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(graphqlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download GraphQL file
  const downloadGraphQL = () => {
    const blob = new Blob([graphqlOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${schemaName.toLowerCase()}.graphql`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to GraphQL Schema Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to GraphQL schema definitions. Paste your JSON data below to generate a GraphQL schema based on the structure.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="schemaName" className="block text-sm font-medium text-gray-700 mb-1">
                Root Type Name
              </label>
              <input
                type="text"
                id="schemaName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={schemaName}
                onChange={handleSchemaNameChange}
                placeholder="Enter root type name (e.g., User, Product)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="generationStyle" className="block text-sm font-medium text-gray-700 mb-1">
                    Schema Generation Style
                  </label>
                  <select
                    id="generationStyle"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={generationStyle}
                    onChange={(e) => setGenerationStyle(e.target.value as SchemaGenerationStyle)}
                  >
                    <option value="basic">Basic (Non-nullable fields)</option>
                    <option value="nullable">Nullable (All fields are nullable)</option>
                    <option value="comprehensive">Comprehensive (CRUD operations)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    'Basic' makes all fields non-nullable, 'Nullable' allows all fields to be null, 'Comprehensive' adds queries and mutations
                  </p>
                </div>
                
                <div>
                  <label htmlFor="namingConvention" className="block text-sm font-medium text-gray-700 mb-1">
                    Field Naming Convention
                  </label>
                  <select
                    id="namingConvention"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={namingConvention}
                    onChange={(e) => setNamingConvention(e.target.value as NamingConvention)}
                  >
                    <option value="PascalCase">PascalCase for types, PascalCase for fields</option>
                    <option value="camelCase">PascalCase for types, camelCase for fields</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToGraphQL}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to GraphQL'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              GraphQL Schema Output
            </label>
            
            {isGraphqlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy GraphQL'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadGraphQL}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isGraphqlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    GraphQL schema will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isGraphqlGenerated && (
              <div className="flex-grow flex flex-col">
                <textarea
                  className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={graphqlOutput}
                  readOnly
                />
              </div>
            )}
          </div>
          
          {isGraphqlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">GraphQL Schema Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated schema includes type definitions derived from your JSON structure. You can use this schema as a starting point for your GraphQL API.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to GraphQL Schema Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data structure and generates a corresponding GraphQL schema. It automatically
              determines field types, creates nested type definitions, and builds a complete schema with queries and, optionally, mutations.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Type Inference</strong> - Automatically detects appropriate GraphQL types based on JSON values</li>
              <li><strong>Nested Object Support</strong> - Creates separate GraphQL types for nested objects</li>
              <li><strong>Array Handling</strong> - Properly defines list types for arrays in your JSON</li>
              <li><strong>Multiple Schema Styles</strong> - Choose between basic, nullable, or comprehensive schema generation</li>
              <li><strong>Naming Conventions</strong> - Control the casing convention used for types and fields</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use representative data in your JSON with realistic values for the most accurate type inference</li>
              <li>Choose 'Comprehensive' generation style for a complete schema with queries and mutations</li>
              <li>For production use, manually review and adjust the generated schema to add custom resolvers and validation</li>
              <li>The converter works best with JSON that represents entity objects rather than service responses</li>
              <li>For complex schemas, you may need to adjust field nullability based on your application requirements</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToGraphQL;